import { useState, useEffect, useRef } from 'react';
import {
  Shield,
  Settings,
  X,
  Save,
  Info,
} from 'lucide-react';
import {
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';

interface AdminAppSettingsProps {
  currentUserId: string;
  currentUserRole: 'root' | 'admin';
  onClose?: () => void;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
}

interface AdminSettings {
  allowAnonymousSignIn: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatedAt: any;
  updatedBy: string;
}

interface BrandingSettings {
  brandName?: string;
  brandTagline?: string;
  logoUrl?: string;
  metaDescription?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatedAt?: any;
  updatedBy?: string;
}

export const AdminAppSettings = ({
  currentUserId,
  onClose,
  showNotify,
  confirmAction,
}: AdminAppSettingsProps) => {
  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [brandingSettings, setBrandingSettings] = useState<BrandingSettings | null>(null);
  const [savingBranding, setSavingBranding] = useState(false);
  const isUpdatingRef = useRef(false);
  const pendingUpdateRef = useRef<boolean | null>(null);

  // Debug: Log state changes
  useEffect(() => {
    console.log('[AdminAppSettings] State updated:', {
      allowAnonymousSignIn: adminSettings?.allowAnonymousSignIn,
      savingSettings,
      loading,
      brandingSettings,
      savingBranding,
      isUpdating: isUpdatingRef.current,
      pendingUpdate: pendingUpdateRef.current
    });
  }, [adminSettings, savingSettings, loading, brandingSettings, savingBranding]);

  // Fetch admin settings
  useEffect(() => {
    const settingsRef = doc(db, 'artifacts', appId, 'settings', 'auth');
    const unsubscribe = onSnapshot(
      settingsRef,
      (docSnap) => {
        // Skip updates while we're saving (to prevent overwriting optimistic update)
        if (isUpdatingRef.current) {
          console.log('[AdminAppSettings] Skipping Firestore update - save in progress');
          return;
        }
        
        // If we have a pending update, use that value instead of Firestore value
        if (pendingUpdateRef.current !== null) {
          console.log('[AdminAppSettings] Using pending update value:', pendingUpdateRef.current);
          const settingsData = docSnap.exists() ? docSnap.data() as AdminSettings : null;
          setAdminSettings({
            allowAnonymousSignIn: pendingUpdateRef.current,
            updatedAt: settingsData?.updatedAt || null,
            updatedBy: settingsData?.updatedBy || currentUserId,
          });
          pendingUpdateRef.current = null; // Clear pending update
          setLoading(false);
          return;
        }
        
        if (docSnap.exists()) {
          const settingsData = docSnap.data() as AdminSettings;
          console.log('[AdminAppSettings] Settings updated from Firestore:', settingsData);
          setAdminSettings(settingsData);
        } else {
          // Default settings if document doesn't exist
          const defaultSettings = {
            allowAnonymousSignIn: true, // Default to enabled
            updatedAt: serverTimestamp(),
            updatedBy: 'system',
          };
          console.log('[AdminAppSettings] No settings found, using defaults:', defaultSettings);
          setAdminSettings(defaultSettings);
        }
        setLoading(false);
      },
      (error) => {
        console.error('[AdminAppSettings] Error fetching admin settings:', error);
        // Set default settings on error
        setAdminSettings({
          allowAnonymousSignIn: true,
          updatedAt: serverTimestamp(),
          updatedBy: 'system',
        });
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [currentUserId]);

  // Fetch branding settings
  useEffect(() => {
    const brandingRef = doc(db, 'artifacts', appId, 'settings', 'branding');

    const unsubscribeBranding = onSnapshot(
      brandingRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as BrandingSettings;
          console.log('[AdminAppSettings] Branding settings updated from Firestore:', data);
          setBrandingSettings(data);
        } else {
          const defaults: BrandingSettings = {
            brandName: 'VerifyPH',
            brandTagline: 'Citizen Reporting & Project Verification',
          };
          console.log(
            '[AdminAppSettings] No branding settings found, using defaults:',
            defaults
          );
          setBrandingSettings(defaults);
        }
      },
      (error) => {
        console.error('[AdminAppSettings] Error fetching branding settings:', error);
        setBrandingSettings({
          brandName: 'VerifyPH',
          brandTagline: 'Citizen Reporting & Project Verification',
        });
      }
    );

    return () => unsubscribeBranding();
  }, []);

  // Update anonymous sign-in setting
  const updateAnonymousSignInSetting = async (enabled: boolean) => {
    // If confirmAction is available, use it; otherwise proceed directly
    const executeUpdate = async () => {
      // Set flags first to prevent Firestore listener from overwriting
      isUpdatingRef.current = true;
      pendingUpdateRef.current = enabled;
      
      // Optimistic UI update - update state immediately for responsive feel
      const previousSettings = adminSettings;
      
      // Create a completely new object to ensure React detects the change
      const newSettings: AdminSettings = {
        allowAnonymousSignIn: enabled,
        updatedAt: previousSettings?.updatedAt || null,
        updatedBy: currentUserId,
      };
      
      console.log('[AdminAppSettings] Optimistic update:', { 
        previous: previousSettings?.allowAnonymousSignIn, 
        new: enabled,
        newSettings,
        pendingUpdate: pendingUpdateRef.current,
        willUpdateTo: newSettings.allowAnonymousSignIn
      });
      
      // Force React to re-render by creating a completely new object with explicit properties
      // Use functional update to ensure we're working with the latest state
      setAdminSettings((prev) => {
        const newState = {
          allowAnonymousSignIn: enabled,
          updatedAt: prev?.updatedAt || null,
          updatedBy: currentUserId,
        };
        console.log('[AdminAppSettings] Setting state to:', newState);
        return newState;
      });
      
      // Force a re-render by updating saving state
      setSavingSettings(true);
      
      try {
        const settingsRef = doc(db, 'artifacts', appId, 'settings', 'auth');
        await setDoc(
          settingsRef,
          {
            allowAnonymousSignIn: enabled,
            updatedAt: serverTimestamp(),
            updatedBy: currentUserId,
          },
          { merge: true }
        );
        
        console.log(`[AdminAppSettings] Successfully saved: Anonymous sign-in ${enabled ? 'enabled' : 'disabled'}`);
        
        if (showNotify) {
          showNotify(
            `Anonymous sign-in ${enabled ? 'enabled' : 'disabled'} successfully`,
            'success'
          );
        }
        
        // Allow Firestore listener to update state with server timestamp
        // Wait a bit longer to ensure the write is complete and listener has processed
        setTimeout(() => {
          isUpdatingRef.current = false;
          // Keep pendingUpdateRef until listener confirms the update
        }, 1000);
      } catch (error) {
        console.error('[AdminAppSettings] Error updating settings:', error);
        isUpdatingRef.current = false;
        pendingUpdateRef.current = null;
        
        // Revert optimistic update on error
        if (previousSettings) {
          setAdminSettings(previousSettings);
        }
        
        if (showNotify) {
          showNotify('Failed to update settings. Please try again.', 'error');
        } else {
          alert(`Failed to update settings: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      } finally {
        setSavingSettings(false);
      }
    };

    // Use confirmation dialog if available, otherwise proceed directly
    if (confirmAction && showNotify) {
      confirmAction(
        enabled ? 'Enable Anonymous Sign-In?' : 'Disable Anonymous Sign-In?',
        enabled
          ? 'This will allow users to access the app without signing in with Google or Email. They will be able to submit reports anonymously.'
          : 'This will require all users to sign in with Google or Email before accessing the app. Existing anonymous users will be signed out.',
        executeUpdate
      );
    } else {
      // Proceed without confirmation if confirmAction is not available
      executeUpdate();
    }
  };

  const isModalMode = !!onClose;

  // Compute toggle state explicitly - handle null/undefined properly
  // This ensures consistent state reading throughout the component
  const isAnonymousSignInEnabled = adminSettings 
    ? (adminSettings.allowAnonymousSignIn === true) 
    : true; // Default to enabled if settings not loaded

  const settingsContent = (
    <div className={`${isModalMode ? 'overflow-y-auto p-6' : ''}`}>
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading settings...</div>
      ) : (
        <div className="space-y-6">
          {/* Page Header */}
          {!isModalMode && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="text-blue-600" size={28} />
                <h1 className="text-3xl font-bold text-gray-900">App Settings</h1>
              </div>
              <p className="text-gray-600">
                Configure application-wide settings and authentication options
              </p>
            </div>
          )}

          {/* Authentication Settings */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield size={20} />
              Authentication Settings
            </h3>
            
            {/* Anonymous Sign-In Toggle */}
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-gray-800">Anonymous Sign-In</h4>
                    {isAnonymousSignInEnabled ? (
                      <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded">
                        Enabled
                      </span>
                    ) : (
                      <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded">
                        Disabled
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Allow users to access the app without signing in with Google or Email. 
                    Anonymous users can browse and submit reports without creating an account.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                    <div className="flex items-start gap-2">
                      <Info className="text-blue-600 shrink-0 mt-0.5" size={16} />
                      <div className="text-xs text-blue-800">
                        <p className="font-semibold mb-1">Impact:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          {isAnonymousSignInEnabled ? (
                            <>
                              <li>Users can access the app immediately without authentication</li>
                              <li>Reports can be submitted anonymously</li>
                              <li>Users can upgrade to authenticated accounts later</li>
                            </>
                          ) : (
                            <>
                              <li>All users must sign in with Google or Email</li>
                              <li>Anonymous users will be signed out</li>
                              <li>Better user tracking and accountability</li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!adminSettings || savingSettings || loading) {
                      console.warn('[AdminAppSettings] Toggle disabled:', { 
                        adminSettings: !!adminSettings, 
                        savingSettings, 
                        loading 
                      });
                      return;
                    }
                    const currentValue = isAnonymousSignInEnabled;
                    const newValue = !currentValue;
                    console.log('[AdminAppSettings] Toggle clicked:', { 
                      current: currentValue, 
                      new: newValue,
                      adminSettingsValue: adminSettings.allowAnonymousSignIn,
                      computedValue: isAnonymousSignInEnabled
                    });
                    updateAnonymousSignInSetting(newValue);
                  }}
                  disabled={savingSettings || loading || !adminSettings}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shrink-0 ${
                    isAnonymousSignInEnabled
                      ? 'bg-blue-600'
                      : 'bg-gray-300'
                  } ${savingSettings || loading || !adminSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
                  aria-label={`${isAnonymousSignInEnabled ? 'Disable' : 'Enable'} anonymous sign-in`}
                  type="button"
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                      isAnonymousSignInEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {savingSettings && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Save size={16} className="animate-spin" />
                  <span>Saving changes...</span>
                </div>
              )}

              {adminSettings?.updatedBy && adminSettings.updatedBy !== 'system' && (
                <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                  Last updated by admin on{' '}
                  {adminSettings.updatedAt?.toDate
                    ? adminSettings.updatedAt.toDate().toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'Unknown date'}
                </div>
              )}
            </div>
          </div>

          {/* Future Settings Section */}
          {/* Branding Settings Section */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Settings size={20} />
              Branding & Meta Settings
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Configure the public branding of the portal. These values control the logo text,
              tagline, and how the site appears in browser tabs and search results.
            </p>

            <form
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!brandingSettings || savingBranding) return;

                try {
                  setSavingBranding(true);
                  const brandingRef = doc(db, 'artifacts', appId, 'settings', 'branding');
                  await setDoc(
                    brandingRef,
                    {
                      brandName: brandingSettings.brandName || 'VerifyPH',
                      brandTagline: brandingSettings.brandTagline || 'Citizen Reporting & Project Verification',
                      logoUrl: brandingSettings.logoUrl || '',
                      metaDescription: brandingSettings.metaDescription || '',
                      updatedAt: serverTimestamp(),
                      updatedBy: currentUserId,
                    },
                    { merge: true }
                  );

                  if (showNotify) {
                    showNotify('Branding settings updated successfully', 'success');
                  }
                } catch (error) {
                  console.error('[AdminAppSettings] Error saving branding settings:', error);
                  if (showNotify) {
                    showNotify(
                      'Failed to update branding settings. Please try again.',
                      'error'
                    );
                  } else {
                    alert(
                      `Failed to update branding settings: ${
                        error instanceof Error ? error.message : 'Unknown error'
                      }`
                    );
                  }
                } finally {
                  setSavingBranding(false);
                }
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label
                    htmlFor="brandName"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Brand Name
                  </label>
                  <input
                    id="brandName"
                    type="text"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="VerifyPH"
                    value={brandingSettings?.brandName ?? ''}
                    onChange={(e) =>
                      setBrandingSettings((prev) => ({
                        ...(prev || {}),
                        brandName: e.target.value,
                      }))
                    }
                  />
                  <p className="text-xs text-gray-500">
                    Shown in the header, footer, and browser title.
                  </p>
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor="brandTagline"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Brand Tagline
                  </label>
                  <input
                    id="brandTagline"
                    type="text"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="Citizen Reporting & Project Verification"
                    value={brandingSettings?.brandTagline ?? ''}
                    onChange={(e) =>
                      setBrandingSettings((prev) => ({
                        ...(prev || {}),
                        brandTagline: e.target.value,
                      }))
                    }
                  />
                  <p className="text-xs text-gray-500">
                    Short phrase shown under the brand name and in the footer.
                  </p>
                </div>
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="logoUrl"
                  className="block text-sm font-medium text-gray-700"
                >
                  Logo Image URL (optional)
                </label>
                <input
                  id="logoUrl"
                  type="url"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  placeholder="https://example.com/logo.png"
                  value={brandingSettings?.logoUrl ?? ''}
                  onChange={(e) =>
                    setBrandingSettings((prev) => ({
                      ...(prev || {}),
                      logoUrl: e.target.value,
                    }))
                  }
                />
                <p className="text-xs text-gray-500">
                  Optional logo image. Future updates can use this for custom logo components.
                </p>
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="metaDescription"
                  className="block text-sm font-medium text-gray-700"
                >
                  Meta Description
                </label>
                <textarea
                  id="metaDescription"
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  placeholder="Short description of the portal used for SEO and link previews."
                  value={brandingSettings?.metaDescription ?? ''}
                  onChange={(e) =>
                    setBrandingSettings((prev) => ({
                      ...(prev || {}),
                      metaDescription: e.target.value,
                    }))
                  }
                />
                <p className="text-xs text-gray-500">
                  Used to populate the page meta description for SEO and social sharing.
                </p>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <Info size={14} className="text-blue-600" />
                  <span>
                    Changes take effect immediately across the public site.
                  </span>
                </div>
                <button
                  type="submit"
                  disabled={savingBranding || !brandingSettings}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${
                    savingBranding || !brandingSettings
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  <Save
                    size={16}
                    className={savingBranding ? 'animate-spin' : ''}
                  />
                  {savingBranding ? 'Saving...' : 'Save Branding'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  // If modal mode, wrap in modal container
  if (isModalMode) {
    return (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0">
              <div className="flex items-center gap-2">
                <Settings className="text-blue-600" size={24} />
                <h2 className="text-2xl font-bold text-gray-900">App Settings</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close settings"
              >
                <X size={24} />
              </button>
            </div>
            {settingsContent}
          </div>
        </div>
      </>
    );
  }

  // Otherwise, return as page component
  return <div className="w-full">{settingsContent}</div>;
};
